import { useState, useEffect, memo } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAdminStatus } from '../hooks/useAdminStatus';

const MaintenanceMode = memo(({ children }) => {
  const [loading, setLoading] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const { data: isAdmin, isLoading: isAdminLoading } = useAdminStatus();

  useEffect(() => {
    const checkMaintenanceMode = async () => {
      try {
        // Get settings from the database
        const { data, error } = await supabase
          .from('settings')
          .select('maintenance_mode')
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error fetching maintenance mode:', error);
          setMaintenanceMode(false);
        } else if (data) {
          setMaintenanceMode(data.maintenance_mode || false);
        }
      } catch (err) {
        console.error('Error checking maintenance mode:', err);
        setMaintenanceMode(false);
      } finally {
        setLoading(false);
      }
    };

    checkMaintenanceMode();
    // Only check maintenance mode when mounting
  }, []);

  if (loading || isAdminLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
          <p className="mt-4 text-indigo-600 dark:text-indigo-400 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // If the site is in maintenance mode and the user is not an admin, show maintenance page
  if (maintenanceMode && !isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white dark:bg-slate-900 px-4">
        <svg
          className="w-24 h-24 text-indigo-600 dark:text-indigo-400 mb-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
          ></path>
        </svg>
        <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-4">Site Under Maintenance</h1>
        <p className="text-lg text-slate-600 dark:text-slate-300 text-center max-w-lg mb-8">
          We&apos;re currently performing some scheduled maintenance. We&apos;ll be back shortly!
        </p>
        <div className="flex space-x-4">
          <a
            href="mailto:support@busbyjersey.shop"
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white rounded-lg transition-colors"
          >
            Contact Support
          </a>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 border border-indigo-600 dark:border-indigo-500 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
          >
            Refresh
          </button>
        </div>
        <p className="mt-4 text-gray-600 dark:text-gray-400">
          If you need immediate assistance, please contact us at{' '}
          <a 
            href="mailto:support@busbyjersey.shop" 
            className="text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            support@busbyjersey.shop
          </a>
        </p>
      </div>
    );
  }

  return children;
});

MaintenanceMode.displayName = 'MaintenanceMode';

export default MaintenanceMode; 